import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import './LoginPage.css';

/**
 * LoginPage — Premium login screen for JohnicornOS.
 * Supports Google OAuth, GitHub OAuth, and Email magic link.
 */
export function LoginPage() {
  const [searchParams] = useSearchParams();
  const accountDeleted = searchParams.get('account_deleted') === '1';
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const signInWithGoogle = useAuthStore((s) => s.signInWithGoogle);
  const signInWithGitHub = useAuthStore((s) => s.signInWithGitHub);
  const signInWithEmail = useAuthStore((s) => s.signInWithEmail);
  const isLoading = useAuthStore((s) => s.isLoading);
  const error = useAuthStore((s) => s.error);
  const clearError = useAuthStore((s) => s.clearError);

  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    await signInWithEmail(email.trim());
    setEmailSent(true);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Logo & Title */}
        <div className="login-header">
          <div className="login-logo">
            <span className="login-logo-emoji">🦄</span>
          </div>
          <h1 className="login-title">JohnicornOS</h1>
          <p className="login-subtitle">Your personal operating system for intentional living</p>
        </div>

        {accountDeleted && (
          <div className="login-banner login-banner--success" role="status">
            <span>Your account was deleted successfully.</span>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="login-error" onClick={clearError}>
            <span>{error}</span>
            <button className="login-error-close">×</button>
          </div>
        )}

        {/* Email magic link sent */}
        {emailSent ? (
          <div className="login-email-sent">
            <div className="login-email-sent-icon">✉️</div>
            <h2 className="login-email-sent-title">Check your email</h2>
            <p className="login-email-sent-text">
              We sent a magic link to <strong>{email}</strong>. 
              Click the link in the email to sign in.
            </p>
            <button 
              className="login-email-sent-retry"
              onClick={() => { setEmailSent(false); clearError(); }}
            >
              Use a different method
            </button>
          </div>
        ) : (
          <>
            {/* OAuth Buttons */}
            <div className="login-oauth-buttons">
              <button
                id="btn-google-login"
                className="login-oauth-btn login-oauth-google"
                onClick={signInWithGoogle}
                disabled={isLoading}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23Z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62Z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53Z" fill="#EA4335"/>
                </svg>
                <span>Continue with Google</span>
              </button>

              <button
                id="btn-github-login"
                className="login-oauth-btn login-oauth-github"
                onClick={signInWithGitHub}
                disabled={isLoading}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                  <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2Z"/>
                </svg>
                <span>Continue with GitHub</span>
              </button>
            </div>

            {/* Divider */}
            <div className="login-divider">
              <div className="login-divider-line" />
              <span className="login-divider-text">or</span>
              <div className="login-divider-line" />
            </div>

            {/* Email form */}
            <form className="login-email-form" onSubmit={handleEmailSubmit}>
              <input
                id="input-email-login"
                type="email"
                className="login-email-input"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="email"
              />
              <button
                id="btn-email-login"
                type="submit"
                className="login-email-submit"
                disabled={isLoading || !email.trim()}
              >
                {isLoading ? (
                  <div className="login-spinner" />
                ) : (
                  'Send magic link'
                )}
              </button>
            </form>
          </>
        )}

        {/* Footer */}
        <p className="login-footer">
          No password needed · Your data stays private · Powered by Supabase
        </p>
        <p className="login-legal">
          <Link to="/terms">Terms of Service</Link>
          {' · '}
          <Link to="/privacy">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
}
